import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRightIcon, CheckIcon, ClockIcon } from "lucide-react";
import { Reveal } from "../Reveal";
import { useCart } from "../../contexts/CartContext";
import { products } from "../../data/products";
import { formatPrice } from "../../utils/currency";
export function ProductSpotlight() {
  const product = products.find(p => p.slug === "pink-pussycat") ?? products[0];
  const {
    addItem
  } = useCart();
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-4, 4]);
  return <section className="relative overflow-hidden border-y border-line bg-surface py-20 lg:py-28" aria-labelledby="spotlight-title" data-id="element-277" data-mp="3gs5retsy1jf0">
      <p className="text-outline pointer-events-none absolute -right-10 top-10 select-none whitespace-nowrap font-display text-[160px] font-extrabold leading-none lg:text-[220px]" aria-hidden="true" data-id="element-278" data-mp="3i8m4pdkgq0uo">
        For her
      </p>
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-20" data-id="element-279" data-mp="38hhqwh6iwm1f">
        <div ref={ref} className="relative" data-id="element-280" data-mp="2ylohv7s0jaoe">
          <div className="relative aspect-square overflow-hidden rounded-[32px] bg-white" data-id="element-281" data-mp="2ympx7og2ajbx">
            <AnimatePresence mode="wait" initial={false} data-id="element-282" data-mp="2uj088xvh5zqd">
              <motion.img key={active} src={product.images[active]} alt={product.name} style={{
              y,
              rotate
            }} initial={{
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25
            }} className="absolute inset-0 h-full w-full object-contain p-8" data-id="element-283" data-mp="2pkyp6cy1u0ew" />
            </AnimatePresence>
          </div>
          <div className="absolute -bottom-6 left-6 flex items-center gap-3 rounded-2xl border border-line bg-night px-5 py-4 shadow-2xl sm:left-10" data-id="element-284" data-mp="2i1h3ou18v0fp">
            <ClockIcon className="h-5 w-5 text-brand" aria-hidden="true" data-id="element-285" data-mp="23dvxusczin43" />
            <div data-id="element-286" data-mp="28autjfcar24e">
              <p className="text-sm font-semibold" data-id="element-287" data-mp="28cfmx1mvkf0o">Take ~1 hour before</p>
              <p className="text-xs text-subtle" data-id="element-288" data-mp="2w0p75z4fwupp">One single-use sachet</p>
            </div>
          </div>
        </div>

        <Reveal data-id="element-289" data-mp="30za2pl8qyq7t">
          <p className="text-sm font-semibold text-ember" data-id="element-290" data-mp="30zteek7t0ide">In the spotlight</p>
          <h2 id="spotlight-title" className="mt-3 font-display text-5xl font-extrabold tracking-tight sm:text-6xl" data-id="element-291" data-mp="2m210txlti41u">
            {product.name}
          </h2>
          <p className="mt-5 max-w-lg leading-relaxed text-white/75" data-id="element-292" data-mp="1anvsytihha18">{product.description}</p>

          <ul className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2" data-id="element-293" data-mp="1fmyy0dygrg97">
            {product.benefits.slice(0, 4).map(b => <li key={b} className="flex items-start gap-3 text-sm" data-id="element-294" data-mp="3tu8707010gr6">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-night" data-id="element-295" data-mp="3ovn9ifc5sb6f">
                  <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-296" data-mp="6qjx09jv4wvz" />
                </span>
                {b}
              </li>)}
          </ul>

          <div className="mt-8 flex gap-3" data-id="element-297" data-mp="2mcbsmcm5z2i">
            {product.images.map((img, i) => <button key={img} type="button" onClick={() => setActive(i)} aria-label={`Show image ${i + 1}`} aria-pressed={active === i} className={`h-16 w-16 overflow-hidden rounded-xl bg-white transition-[opacity,box-shadow] duration-200 ${active === i ? "ring-2 ring-brand ring-offset-2 ring-offset-surface" : "opacity-60 hover:opacity-100"}`} data-id="element-298" data-mp="3b25qcvndx0gx" data-mpi={i}>
                <img src={img} alt="" className="h-full w-full object-contain" data-id="element-299" data-mp="2ncl5g77o7t50" />
              </button>)}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4" data-id="element-300" data-mp="352q8mg3f7j3i">
            <span className="font-display text-4xl font-extrabold text-brand" data-id="element-301" data-mp="350x3544jn4x3">{formatPrice(product.price)}</span>
            <button type="button" onClick={() => addItem(product.slug)} className="rounded-full bg-brand px-8 py-4 text-sm font-bold text-night transition-colors hover:bg-white active:scale-[0.98]" data-id="element-302" data-mp="3a09qdpzbue07">
              Add to cart
            </button>
            <Link to={`/product/${product.slug}`} className="group inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white" data-id="element-303" data-mp="3sskluyrnj9oa">
              Details
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-304" data-mp="1yf2ut1s1fuoi" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>;
}